<template>
  <div class="body application-details-container">
    <div class="details">
      <div class="configuration">
        <section
          :class="$style.sectionRoot "
          :data-test-id="$testId('sectionRoot')"
        >
          <header
            :class="[
              $style.sectionHeader,
              $style.sectionGeneralMargin
            ]"
          >
            Application Information
          </header>
          <InputText
            v-model="displayLabel"
            :class="$style.inputComponent"
            :data-test-id="$testId('displayLabelInput')"
            label="Display Label"
            name="Display Label"
            required
            rules="required|isDisplayLabelValid"
          />
          <Textarea
            v-model="description"
            :class="$style.inputComponent"
            :data-test-id="$testId('descriptionInput')"
            fitContent
            label="Description"
            name="Description"
            :placeholder="descriptionPlaceholder"
            rules="max:256"
          />
          <ApplicationAvatarChooser
            :parentType="parentType"
          />
          <ApplicationVisibility
            v-if="$hasFeatureFlag($featureFlags.ssoHiddenApps)"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable import/order */
import * as Constants from 'client/util/Constants';
import { InputText, Textarea, addValidationRule } from '@jumpcloud/ui-components';
import { extend } from 'vee-validate3';
import { mapActions, mapGetters, mapState } from 'vuex'; import { notify } from 'client/util';
import { validationRules } from 'client/applications/utils/ApplicationUtils';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import ApplicationAvatarChooser from 'client/applications/ApplicationAvatarChooser.vue';
import ApplicationConfigCertMixin from 'client/applications/mixins/ApplicationConfigCertMixin';
import ApplicationVisibility from 'client/applications/ApplicationVisibility.vue';
import AsidePanelChildComponentMethodsMixin from '../components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from '../components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import IconTooltipSettings from 'client/components/Tooltip/IconTooltipSettings';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

const { isDisplayLabelValid } = validationRules;
const { error, success } = Constants.default;
const { ItemTypeConsts, StoreModelMap, StoreModuleMap } = Constants;
const applicationModel = StoreModelMap[ItemTypeConsts.application];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const validators = [isDisplayLabelValid];
validators.forEach(validator => {
  extend(validator.name, validator.config);
  addValidationRule(validator.name, validator.config);
});

export default {
  name: 'ApplicationGeneralInfoConfiguration',

  components: {
    ApplicationAvatarChooser,
    ApplicationVisibility,
    InputText,
    Textarea,
  },

  mixins: [
    ApplicationConfigCertMixin,
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  learnMore: {
    infoText: 'To learn more about this configuration, including restricting access to specific users, please visit our',
    kbText: 'Knowledge Base',
  },

  data() {
    const descriptionPlaceholder = 'Add a description that users will see for this app in their User Portal.';
    const fileInputTooltip = {
      kbLink: KbArticleUrls.ssoWithSamlConnector,
      text: IconTooltipSettings.spMetadataText,
    };

    return {
      descriptionPlaceholder,
      fileInputTooltip,
    };
  },

  computed: {
    ...mapGetters(settingsModule, { betaFeatures: 'getBetaFeatures' }),

    ...mapState(settingsModule, ['orgId']),

    ...mapGetters(applicationModel, [
      'isItemBeingCreated',
      'isItemBeingUpdated',
    ]),

    ...mapState(applicationModel, [
      'template',
    ]),

    description: {
      get() {
        return this.item.description;
      },
      set(value) {
        this.setItemField({ key: 'description', value });
      },
    },

    displayLabel: {
      get() {
        return this.item.displayLabel;
      },
      set(value) {
        this.setItemField({ key: 'displayLabel', value });
      },
    },
  },

  methods: {
    ...mapActions(applicationModel, [
      'setItemField',
    ]),
  },

  handleApplicationState() {
    const status = !this.item.active;
    this.setItemField({ key: 'active', value: status });
  },

  handleFileChange(e, reader = new FileReader()) {
    const input = e.target;
    const { item } = this;
    const { files } = input;

    // Prepare the reader for persistence of the base64 file...
    // eslint-disable-next-line no-param-reassign
    reader.onload = (event) => {
      const xml = event.target.result;
      const attributes = this.cleanXmlModelMetadata(item);

      ApplicationApi.postSPMetadata(xml, attributes)
        .then((res) => {
          this.setItemField({ key: 'config', value: this.cleanObject(res.config) });
          this.dynamicComponentKey += 1;
          notify(success, 'Successfully read service provider metadata file.');
        })
        .catch((err) => {
          notify(error, err.message);
        });
    };

    if (files.length > 0) {
      const { size } = files[0];

      if (size > 1024 * 1024) {
        notify(error, 'Files uploaded must be smaller than 1 MB.');
        return;
      }
      reader.readAsText(files[0]);
    }
  },
};
</script>

<style module>
@import '../css/sections.css';

.sectionGeneralMargin {
  margin-bottom: 20px !important;
}

.inputComponent {
  margin-bottom: var(--jc-spacer);
}

</style>
